import { SHContainer } from "@components/design-systems";
import { StepName } from "@layouts/stepper/step-name";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { AnalysisSetupStepSectionsConfig as ConfigSections } from "@pages/platform-analysis-v2/_id/steps/setup/configs";
import {
  UserPermissionsReturnType,
  useUserPermissions,
} from "@hooks/userUserPermission";
import { AnalysisSetupStepSection } from "@pages/platform-analysis-v2/_id/steps/setup/components/section";
import { AnalysisUserType } from "@models/platform-analysis/entities/analysis";
import { FormProvider, useForm } from "react-hook-form";
import { AnalysisSetupDTO } from "@models/platform-analysis/entities/steps/setup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useEffectOnce, useToggle } from "react-use";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { useIsNew } from "@hooks/useIsNew";
import { generatePath, useNavigate, useParams } from "react-router";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  getAnalysisStepSetupValidators,
  getUpdateAnalysisSetupData,
} from "@pages/platform-analysis-v2/_id/steps/setup/utils";
import { AnalysisSetupSkeleton } from "@pages/platform-analysis-v2/components/skeletons/setup";
import { ConfirmDiscardStepsDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/confirm-discard-steps";
import { cloneDeep, isEmpty } from "lodash";
import { PageRoutes } from "@constants";

interface AnalysisSetupStepProps extends StepCompProps {}

export const AnalysisSetupStep: ForwardRefRenderFunction<
  StepCompRef,
  AnalysisSetupStepProps
> = ({ stepNumber }, ref) => {
  const isNew = useIsNew();
  const navigate = useNavigate();
  const userPermissions: UserPermissionsReturnType = useUserPermissions();
  const { isSupplierGroup } = userPermissions;
  const {
    setPlatformAnalysis,
    loadPlatformAnalysis,
    updateCurrentPlatformAnalysis,
    createNewPlatformAnalysis,
    setAnalysisDiscardedStages,
    loadPlatformAnalysisStep,
    setIsDirty,
    discardedStages,
    platformAnalysis,
    isDirty,
    isCompleted,
    isReadOnly,
  } = usePlatformAnalysis();
  const { analysisId } = useParams<{ analysisId: string }>();

  const userType: AnalysisUserType = isSupplierGroup ? "supplier" : "adviser";

  const formMethods = useForm<AnalysisSetupDTO>({
    mode: "onChange",
    defaultValues: { ...new AnalysisSetupDTO() },
    resolver: yupResolver(getAnalysisStepSetupValidators(userType)),
  });
  const { getValues, reset } = formMethods;

  useImperativeHandle(ref, () => ({
    onChangeStep: async () => await handleOnSubmit(),
    onChangeOtherStep: async () => true,
    onSubmit: async () => await handleOnSubmit(),
  }));

  const pendingNavigateRef = useRef<string | null>(null);

  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [isLoadingPlatformAnalysis, toggleLoadingPlatformAnalysis] =
    useToggle(true);
  const [confirmStageChange, setConfirmStageChange] = useState<
    | {
        callback: (value: boolean | PromiseLike<boolean>) => void;
      }
    | undefined
  >(undefined);

  const handleCreateAnalysis = async (formData: AnalysisSetupDTO) => {
    const newAnalysis = await createNewPlatformAnalysis(formData);
    setIsDirty(!newAnalysis);
    if (!newAnalysis) return false;
    newAnalysis?.setup && reset(newAnalysis?.setup);
    pendingNavigateRef.current = newAnalysis.id;

    setTimeout(() => loadPlatformAnalysisStep(newAnalysis.id), 0);

    return true;
  };

  const handleUpdateAnalysis = async (formData: AnalysisSetupDTO) => {
    if (!isDirty) return true;
    if (!platformAnalysis) return false;
    const { updatedSetupData, updatedAnalysis } = getUpdateAnalysisSetupData({
      setupData: formData,
      originalData: platformAnalysis,
    });

    const response = await updateCurrentPlatformAnalysis(updatedAnalysis);

    if (!response) return false;

    reset(updatedSetupData);
    setPlatformAnalysis({
      ...platformAnalysis,
      setup: updatedSetupData,
      configurationVersion: response
        ? response.configurationVersion
        : platformAnalysis?.configurationVersion,
    });

    setTimeout(() => loadPlatformAnalysisStep(response.id), 0);

    return true;
  };

  const getConfirmStageChange = async (): Promise<boolean> => {
    return await new Promise((res) => {
      setConfirmStageChange({
        callback: res,
      });
    });
  };

  const handleOnSubmit = async () => {
    if (isCompleted) return true;
    toggleIsSubmitting(true);
    try {
      if (!isEmpty(discardedStages)) {
        const isYes = await getConfirmStageChange();
        setConfirmStageChange(undefined);
        if (!isYes) {
          return false;
        }
        setAnalysisDiscardedStages([]);
      }
      const formData = cloneDeep(getValues());

      if (isNew) return await handleCreateAnalysis(formData);

      return await handleUpdateAnalysis(formData);
    } finally {
      toggleIsSubmitting(false);
    }
  };

  useEffectOnce(() => {
    if (isNew) {
      toggleLoadingPlatformAnalysis(false);

      return;
    }

    if (analysisId) {
      loadPlatformAnalysis({
        platformAnalysisId: analysisId,
        stepId: AnalysisStep.Setup,
      }).then((analysis) => {
        if (analysis?.setup) reset(analysis.setup);
        toggleLoadingPlatformAnalysis(false);
      });
    }
  });

  useEffect(() => {
    if (!pendingNavigateRef.current) return;
    navigate(
      generatePath(PageRoutes.platformAnalysisDetail, {
        analysisId: pendingNavigateRef.current,
      }),
      {
        replace: true,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNavigateRef.current]);

  const shouldDisableForm = useMemo(
    () => isSubmitting || isCompleted || isReadOnly,
    [isCompleted, isReadOnly, isSubmitting],
  );

  if (isLoadingPlatformAnalysis) return <AnalysisSetupSkeleton />;

  return (
    <SHContainer sx={{ px: { xs: "16px", lg: 0 }, py: "25px" }}>
      <FormProvider {...formMethods}>
        <form>
          <StepName
            name={isSupplierGroup ? "Analysis setup" : "Review setup"}
            stepNumber={stepNumber}
          />

          {ConfigSections.map((section, index) => {
            const { showFor, specificPermissions } = section;
            const validUserType = showFor.includes(userType);
            const hasPermission = !!specificPermissions?.every(
              (key) => !!userPermissions[key],
            );

            if (!validUserType || (!hasPermission && !!specificPermissions))
              return null;

            return (
              <AnalysisSetupStepSection
                key={index}
                showDivider={index !== 0}
                section={section}
                userType={userType}
                disabled={shouldDisableForm}
              />
            );
          })}
        </form>

        {confirmStageChange && (
          <ConfirmDiscardStepsDialog
            open={true}
            discardedStages={discardedStages ?? []}
            onDiscard={() => confirmStageChange.callback(true)}
            onClose={() => confirmStageChange.callback(false)}
          />
        )}
      </FormProvider>
    </SHContainer>
  );
};
